/**
 * Organism: TaskMaterialsSection
 *
 * Materials requirements list for a task.
 * - Live search from cost catalogue
 * - Stock status ring per material
 * - Tracking pill for shipped items
 * - "Order missing materials" CTA
 * - Materials subtotal
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { authedFetch } from '@/lib/api'
import { formatCurrency } from '@/utils/formatCurrency'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '@/lib/usePermissions'

// ── Types ─────────────────────────────────────────────────────────────────────

export type StockStatus = 'in_stock' | 'low' | 'not_in_stock' | 'po_issued' | 'shipped' | 'received'

interface TaskMaterial {
  id: number
  name: string
  quantity: number
  unit?: string
  unit_cost?: number
  line_total?: number
  stock_status: StockStatus
  tracking_number?: string
  po_id?: number
  po_status?: string
  /** Matches a consumable_items row (by catalogue_item_id FK or name). When
   *  false, the UI shows the "Add to inventory" chip. */
  is_tracked?: boolean
  catalogue_item_id?: number
  /** Live state on the shared Procurement Tracker. `null`/absent = not queued.
   *  `draft_task_material` = auto-queued on the tracker awaiting confirm.
   *  Otherwise a stage key (needs_ordering, bid_request, po_issued, …). */
  procurement_status?: string | null
}

// Human label for each tracker stage; used for the status pill.
// Exported (UOW-1A #3586): the canonical task peek's Sourcing facet renders
// the same stage pill — one vocabulary, one styling source (this file).
export const PROCUREMENT_STATUS_LABEL: Record<string, string> = {
  // UOW-5A (§3.12): 'draft' is the birth state of an auto-drafted sourcing
  // requirement — first-class, not the legacy NULL-coalesce it used to be.
  draft: "Draft",
  draft_task_material: "Draft — review on tracker",
  needs_ordering: "Needs ordering",
  bid_request: "Bid request sent",
  quoted: "Quoted",
  awarded: "Awarded",
  po_issued: "PO issued",
  ordered: "Ordered",
  in_transit: "In transit",
  received: "Received",
  delivered: "Delivered",
  cancelled: "Cancelled",
  booked: "Booked",
  in_progress: "In progress",
  completed: "Completed",
}
export const PROCUREMENT_STATUS_COLOR: Record<string, string> = {
  draft:               'var(--st-draft)',
  draft_task_material: 'var(--st-draft)',
  needs_ordering:      'var(--st-warning)',
  bid_request:         'var(--st-active)',
  quoted:              'var(--st-active)',
  awarded:             'var(--st-info)',
  po_issued:           'var(--st-info)',
  ordered:             'var(--st-info)',
  in_transit:          'var(--st-onhold)',
  received:            'var(--st-done)',
  delivered:           'var(--st-done)',
  cancelled:           'var(--st-draft)',
}

/**
 * ProcurementStagePill — the tracker-stage pill, extracted verbatim from the
 * materials-list render below so the canonical task peek (UOW-1A #3586) and
 * this section share ONE pill instead of two divergent copies. Renders null
 * for stages outside the vocabulary.
 */
export function ProcurementStagePill({ status, style = {} }: {
  status: string
  style?: React.CSSProperties
}) {
  const label = PROCUREMENT_STATUS_LABEL[status]
  if (!label) return null
  return (
    <span title="Current state on the Procurement Tracker"
      style={{
        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9999,
        background: `color-mix(in srgb, ${PROCUREMENT_STATUS_COLOR[status] || 'var(--st-draft)'} 13%, transparent)`,
        color: PROCUREMENT_STATUS_COLOR[status] || 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        ...style,
      }}>
      {label}
    </span>
  )
}

interface CatalogueItem {
  id: number
  name: string
  unit: string
  unit_cost: number
  category?: string
}

// ── LineItem type (unified document_line_items shape) ────────────────────────

interface LineItem {
  id: number
  parent_type: string
  parent_id: string
  item_type: string
  name: string
  quantity: number
  unit?: string
  unit_cost: number
  markup_pct: number
  tax_pct: number
  subtotal: number
  total: number
  catalogue_item_id?: number
  task_id?: string
}

function lineItemToTaskMaterial(li: LineItem): TaskMaterial {
  return {
    id: li.id,
    name: li.name,
    quantity: li.quantity,
    unit: li.unit,
    unit_cost: li.unit_cost || undefined,
    line_total: li.total || undefined,
    stock_status: 'not_in_stock',
  }
}

// ── Status ring config ────────────────────────────────────────────────────────
// Exported (UOW-1A #3586): the task peek's stock badge reuses this vocabulary.

export const STOCK_STATUS_CONFIG: Record<StockStatus, { color: string; label: string }> = {
  in_stock:    { color: 'var(--st-active)',  label: 'In Stock' },
  low:         { color: 'var(--st-warning)', label: 'Low Stock' },
  not_in_stock:{ color: 'var(--st-error)',   label: 'Not in Stock' },
  po_issued:   { color: 'var(--st-info)',    label: 'PO Issued' },
  shipped:     { color: 'var(--st-draft)',   label: 'Shipped' },
  received:    { color: 'var(--st-done)',    label: 'Received' },
}

function StatusDot({ status }: { status: StockStatus }) {
  const cfg = STOCK_STATUS_CONFIG[status] || STOCK_STATUS_CONFIG.not_in_stock
  return (
    <span
      title={cfg.label}
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: cfg.color,
        flexShrink: 0,
        marginTop: 2,
      }}
    />
  )
}

function TrackingPill({ trackingNumber }: { trackingNumber: string }) {
  // Guess carrier URL
  const url = trackingNumber.startsWith('1Z')
    ? `https://www.ups.com/track?tracknum=${trackingNumber}`
    : trackingNumber.length === 22
    ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`
    : `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${trackingNumber}`

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 12,
        backgroundColor: 'var(--color-violet-50, #f5f3ff)',
        color: 'var(--color-violet-700, #6d28d9)',
        fontSize: 11,
        fontWeight: 600,
        textDecoration: 'none',
        border: '1px solid var(--color-violet-200, #ddd6fe)',
        whiteSpace: 'nowrap',
      }}
    >
      📦 {trackingNumber.slice(0, 12)}…
    </a>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

interface TaskMaterialsSectionProps {
  taskId: string
  projectId?: number | null
  readOnly?: boolean
}

export function TaskMaterialsSection({ taskId, readOnly = false }: TaskMaterialsSectionProps) {
  const { can } = usePermissions()
  const { t } = useTranslation()
  const [materials, setMaterials] = useState<TaskMaterial[]>([])
  const [subtotal, setSubtotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Add form state
  const [showAdd, setShowAdd] = useState(false)
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState<CatalogueItem[]>([])
  const [newName, setNewName] = useState('')
  const [newQty, setNewQty] = useState('1')
  const [newUnit, setNewUnit] = useState('')
  const [newCost, setNewCost] = useState('')
  const [newCatItemId, setNewCatItemId] = useState<number | null>(null)
  const [saveToCatalogue, setSaveToCatalogue] = useState(false)
  const [saving, setSaving] = useState(false)

  const [orderingPO, setOrderingPO] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadMaterials = useCallback(async () => {
    if (!taskId || taskId === 'undefined' || taskId === 'null') {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await authedFetch(`/api/v1/tasks/${taskId}/materials`, {}, { redirectOnAuthFailure: false })
      if (!res.ok) {
        setMaterials([])
        setSubtotal(0)
        return
      }
      const data = await res.json()
      setMaterials(data.materials || [])
      setSubtotal(data.subtotal || 0)
    } catch {
      setMaterials([])
      setSubtotal(0)
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => { loadMaterials() }, [loadMaterials])

  // Debounced catalogue search
  useEffect(() => {
    if (!showAdd) return
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(async () => {
      if (searchQ.length < 2) { setSearchResults([]); return }
      const res = await authedFetch(`/api/v1/cost-catalogue/search?q=${encodeURIComponent(searchQ)}`, {}, { redirectOnAuthFailure: false })
      if (res.ok) {
        const items: CatalogueItem[] = await res.json()
        setSearchResults(items)
      }
    }, 250)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [searchQ, showAdd])

  const handleSelectCatalogueItem = (item: CatalogueItem) => {
    setNewName(item.name)
    setNewUnit(item.unit || '')
    setNewCost(item.unit_cost ? String(item.unit_cost) : '')
    setNewCatItemId(item.id)
    setSearchQ(item.name)
    setSearchResults([])
  }

  const handleAddMaterial = async () => {
    if (!newName.trim()) return
    setSaving(true)
    try {
      const res = await authedFetch(`/api/v1/tasks/${taskId}/materials`, {
        method: 'POST',
        body: JSON.stringify({
          name: newName.trim(),
          quantity: parseFloat(newQty) || 1,
          unit: newUnit || undefined,
          unit_cost: newCost ? parseFloat(newCost) : undefined,
          catalogue_item_id: newCatItemId || undefined,
          save_to_catalogue: saveToCatalogue,
        }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error('Failed to add material')
      setNewName(''); setNewQty('1'); setNewUnit(''); setNewCost(''); setNewCatItemId(null)
      setSearchQ(''); setSaveToCatalogue(false); setShowAdd(false)
      await loadMaterials()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (matId: number) => {
    if (!confirm('Remove this material?')) return
    await authedFetch(`/api/v1/line-items/${matId}`, { method: 'DELETE' }, { redirectOnAuthFailure: false })
    await loadMaterials()
  }

  const handleCreatePO = async () => {
    // Now that procurement is unified on the tracker, this button queues every
    // not-in-stock material onto the Procurement Tracker (sets
    // procurement_status='needs_ordering') and hops the user to the Tracker
    // view. A real PO is only created later from the Tracker's "Convert to PO"
    // action (Phase 2). No more hard-PO here, no more 404 on /purchase-orders/N.
    setOrderingPO(true)
    try {
      const targets = materials.filter(m =>
        m.stock_status === 'not_in_stock' &&
        m.procurement_status !== 'needs_ordering' &&
        m.procurement_status !== 'po_issued' &&
        m.procurement_status !== 'ordered'
      )
      if (targets.length === 0) {
        alert('Nothing to queue — all materials are already on the tracker or in stock.')
        return
      }
      await Promise.all(targets.map(m =>
        authedFetch(`/api/v1/procurement-tracker/${m.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ procurement_status: 'needs_ordering' }),
        }, { redirectOnAuthFailure: false })
      ))
      await loadMaterials()
      window.location.href = `/procurement?tab=tracker`
    } catch {
      alert('Failed to queue materials on the tracker')
    } finally {
      setOrderingPO(false)
    }
  }

  const missingCount = materials.filter(m => m.stock_status === 'not_in_stock').length

  const handleTrackInInventory = useCallback(async (materialId: number) => {
    try {
      const res = await authedFetch(`/api/v1/line-items/${materialId}/track-in-inventory`, { method: 'POST' }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error('Failed to add to inventory')
      await loadMaterials()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to add to inventory')
    }
  }, [loadMaterials])

  // Queue the material on the Procurement Tracker at "needs_ordering". Works
  // from either state: no status yet (creates fresh entry) or draft awaiting
  // confirm (accepts the draft). Uses the shared tracker PATCH.
  const handleAddToTracker = useCallback(async (materialId: number) => {
    try {
      const res = await authedFetch(`/api/v1/procurement-tracker/${materialId}`, {
        method: 'PATCH',
        body: JSON.stringify({ procurement_status: 'needs_ordering' }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error('Failed to add to procurement tracker')
      await loadMaterials()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to add to tracker')
    }
  }, [loadMaterials])

  return (
    <div style={{ marginTop: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('task_materials.title', 'Materials')}
          </span>
          {materials.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-disabled)', fontWeight: 500 }}>
              ({materials.length})
            </span>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowAdd(v => !v)}
            style={{
              fontSize: 12, fontWeight: 600, color: 'var(--color-brand-600, #2563eb)',
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
            }}
          >
            {showAdd ? '✕ Cancel' : `+ ${t('task_materials.add', 'Add Material')}`}
          </button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{
          background: 'var(--surface-raised, #f8fafc)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 12, marginBottom: 12,
        }}>
          {/* Search / name */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              placeholder={t('task_materials.search_placeholder', 'Search catalogue or type a name…')}
              value={searchQ || newName}
              onChange={e => {
                setSearchQ(e.target.value)
                setNewName(e.target.value)
                setNewCatItemId(null)
              }}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' }}
            />
            {searchResults.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto',
              }}>
                {searchResults.map(item => (
                  <div
                    key={item.id}
                    onClick={() => handleSelectCatalogueItem(item)}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                      borderBottom: '1px solid var(--border-faint)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-hover, #f1f5f9)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span>{item.name}</span>
                    <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>
                      {item.unit} · {formatCurrency(item.unit_cost, { decimals: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Qty / unit / cost row */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              placeholder={t('task_materials.qty', 'Qty')}
              type="number"
              min="0.01"
              step="any"
              value={newQty}
              onChange={e => setNewQty(e.target.value)}
              style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <input
              placeholder={t('task_materials.unit', 'Unit')}
              value={newUnit}
              onChange={e => setNewUnit(e.target.value)}
              style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <input
              placeholder={t('task_materials.unit_cost', 'Unit Cost')}
              type="number"
              min="0"
              step="0.01"
              value={newCost}
              onChange={e => setNewCost(e.target.value)}
              style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
            />
          </div>

          {/* Save to catalogue toggle */}
          {!newCatItemId && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={saveToCatalogue} onChange={e => setSaveToCatalogue(e.target.checked)} />
              {t('task_materials.save_to_catalogue', 'Save to cost catalogue for future use')}
            </label>
          )}

          <button
            onClick={handleAddMaterial}
            disabled={saving || !newName.trim()}
            style={{
              padding: '6px 16px', borderRadius: 6, border: 'none', cursor: saving || !newName.trim() ? 'not-allowed' : 'pointer',
              background: saving || !newName.trim() ? 'var(--bg-elevated)' : 'var(--brand-gold)',
              color: saving || !newName.trim() ? 'var(--text-disabled)' : 'var(--brand-gold-text)',
              fontWeight: 600, fontSize: 13,
            }}
          >
            {saving ? t('common.saving', 'Saving…') : t('task_materials.add_btn', 'Add')}
          </button>
        </div>
      )}

      {/* Materials list */}
      {loading ? (
        <div style={{ color: 'var(--text-disabled)', fontSize: 13 }}>{t('common.loading', 'Loading…')}</div>
      ) : error ? (
        <div style={{ color: 'var(--st-error)', fontSize: 13 }}>{error}</div>
      ) : materials.length === 0 ? (
        <div style={{ color: 'var(--text-disabled)', fontSize: 13, fontStyle: 'italic' }}>
          {t('task_materials.empty', 'No materials listed yet.')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {materials.map(m => {
            const statusCfg = STOCK_STATUS_CONFIG[m.stock_status] || STOCK_STATUS_CONFIG.not_in_stock
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 4px',
                  borderBottom: '1px solid var(--border-faint, #f1f5f9)',
                }}
              >
                <StatusDot status={m.stock_status} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{m.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                      {m.quantity}{m.unit ? ` ${m.unit}` : ''}
                    </span>
                    <span style={{ fontSize: 11, color: statusCfg.color, fontWeight: 600 }}>{statusCfg.label}</span>
                    {m.tracking_number && <TrackingPill trackingNumber={m.tracking_number} />}
                    {m.procurement_status && m.procurement_status !== 'draft_task_material' && (
                      <ProcurementStagePill status={m.procurement_status} />
                    )}
                    {m.procurement_status === 'draft_task_material' && !readOnly && (
                      <button
                        onClick={() => handleAddToTracker(m.id)}
                        title="Confirm this draft and add to the Procurement Tracker"
                        style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 9999,
                          border: 'none', background: 'var(--brand-gold)', color: 'var(--brand-gold-text)', cursor: 'pointer',
                        }}>
                        Accept draft
                      </button>
                    )}
                    {!m.procurement_status && !readOnly && (
                      <button
                        onClick={() => handleAddToTracker(m.id)}
                        title="Surface this material on the company Procurement Tracker"
                        style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 9999,
                          border: '1px dashed var(--border)', background: 'transparent',
                          color: 'var(--text-muted, #6b7280)', cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}>
                        + Add to procurement tracker
                      </button>
                    )}
                    {m.is_tracked === false && !readOnly && can('inventory', 'add') && (
                      <button
                        onClick={() => handleTrackInInventory(m.id)}
                        title="Add this material to the company inventory tracker"
                        style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 9999,
                          border: '1px dashed var(--border)', background: 'transparent',
                          color: 'var(--text-muted, #6b7280)', cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        + Track in inventory
                      </button>
                    )}
                  </div>
                  {m.line_total != null && (
                    <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginTop: 1 }}>
                      {formatCurrency(m.unit_cost, { decimals: 2 })}{m.unit ? `/${m.unit}` : ''} = {formatCurrency(m.line_total, { decimals: 2 })}
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleDelete(m.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-disabled)', fontSize: 14, padding: 2 }}
                    title={t('buttons.delete', 'Delete')}
                  >
                    ✕
                  </button>
                )}
              </div>
            )
          })}

          {/* Subtotal */}
          {subtotal > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'flex-end', paddingTop: 8,
              borderTop: '1px solid var(--border)',
              fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
            }}>
              {t('task_materials.subtotal', 'Est. Materials')}:&nbsp;{formatCurrency(subtotal, { decimals: 2 })}
            </div>
          )}
        </div>
      )}

      {/* Order missing materials CTA */}
      {!readOnly && missingCount > 0 && (
        <button
          onClick={handleCreatePO}
          disabled={orderingPO}
          style={{
            marginTop: 12, width: '100%', padding: '8px 16px', borderRadius: 8,
            border: '1.5px dashed var(--st-error)',
            background: 'color-mix(in srgb, var(--st-error) 6%, transparent)',
            color: 'var(--st-error)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {orderingPO
            ? t('common.working', 'Queueing…')
            : t('task_materials.queue_missing', 'Queue {{count}} missing on procurement tracker', { count: missingCount })
          }
        </button>
      )}
    </div>
  )
}

export default TaskMaterialsSection
